// packages/shared-hooks/src/leads/useMyCallCenterLeads.ts
//
// `GET /call-center/my-leads` (#1396) — the external call-center agent's OWN
// accepted lead-intake submissions, cursor-paginated and newest-first. The
// optional `phone` filter is a SERVER-SIDE substring search over the agent's
// whole history (not the already-loaded rows).
//
// DANGER: this is the EXTERNAL agent boundary. Each row is a BLIND snapshot
// (`CallCenterMyLeadsItemSchema`) carrying no server-side CRM state — never a
// lead id, public code, outcome, manager, or stage. The page renders the row's
// in-memory snapshot only; it never fetches a detail by id.
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { CallCenterMyLeadsResponseSchema, type CallCenterMyLeadsResponse } from '@magimp/shared';
import { useApiClient } from '../api/context.js';
import { leadsKeys } from './keys.js';

export interface MyCallCenterLeadsFilter {
  /** Server-side phone substring filter over the whole history (1..32 chars). */
  phone?: string;
  limit?: number;
}

// WHY: changing the phone filter must start a fresh cursor walk, so `cursor`
// is excluded from the query key and passed through `pageParam` only. Mirrors
// the `useLeadsInfinite` / `useRequestsInfinite` invariant.
export function useMyCallCenterLeads(filter: MyCallCenterLeadsFilter = {}) {
  const client = useApiClient();
  return useInfiniteQuery<
    CallCenterMyLeadsResponse,
    Error,
    InfiniteData<CallCenterMyLeadsResponse>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: leadsKeys.myCallCenterInfinite(filter as Record<string, unknown>),
    initialPageParam: undefined,
    queryFn: ({ pageParam, signal }) =>
      client.get<CallCenterMyLeadsResponse>(
        '/call-center/my-leads',
        {
          signal,
          query: { phone: filter.phone, cursor: pageParam, limit: filter.limit },
        },
        CallCenterMyLeadsResponseSchema,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}
